import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Star, Loader2, MessageSquare, Sparkles } from 'lucide-react';
import { createFeedback, getMyFeedbackStatus } from '../api';

const LOW_CREDITS_THRESHOLD = 5;
const STORAGE_KEY = 'fashdeck:feedbackHandled';

const FeedbackPrompt = ({ imageCredits, showNotification }) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handledRef = useRef(false);
  const creditsSpentRef = useRef(0);
  const prevCreditsRef = useRef(null);
  const sessionStartRef = useRef(Date.now());

  const isLoggedIn = Boolean(typeof window !== 'undefined' && localStorage.getItem('token'));

  const markHandled = useCallback(() => {
    handledRef.current = true;
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch (_) {}
  }, []);

  const tryOpenPrompt = useCallback((remainingCredits) => {
    if (handledRef.current || open) return;
    // First-time feedback: only after credits drop below the low-balance threshold.
    if (Number.isFinite(remainingCredits) && remainingCredits < LOW_CREDITS_THRESHOLD) {
      setOpen(true);
    }
  }, [open]);

  useEffect(() => {
    if (!isLoggedIn) {
      handledRef.current = true;
      return;
    }
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') {
        handledRef.current = true;
        return;
      }
    } catch (_) {}

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await getMyFeedbackStatus(token);
        if (!cancelled && res?.data?.data?.hasSubmitted) {
          markHandled();
        }
      } catch (_) {
        // Non-fatal: fall back to local gating.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, markHandled]);

  useEffect(() => {
    const current = Number(imageCredits);
    if (!Number.isFinite(current)) return;
    const prev = prevCreditsRef.current;
    if (prev !== null && current < prev) {
      creditsSpentRef.current += prev - current;
      tryOpenPrompt(current);
    }
    prevCreditsRef.current = current;
  }, [imageCredits, tryOpenPrompt]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const submit = useCallback(async () => {
    if (rating < 1 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await createFeedback(
        {
          rating,
          message: String(message || '').trim(),
          source: window.location?.hash || window.location?.pathname || '',
          metadata: {
            href: window.location?.href || '',
            creditsSpentSession: creditsSpentRef.current,
            sessionDurationMs: Date.now() - sessionStartRef.current,
            userAgent: navigator.userAgent,
          },
        },
        token
      );
      markHandled();
      setOpen(false);
      showNotification?.('Thanks for your feedback!', 'success');
    } catch (err) {
      if (err?.response?.status === 409) {
        markHandled();
        setOpen(false);
        return;
      }
      showNotification?.(
        err?.response?.data?.message || err?.message || 'Failed to submit feedback',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [rating, message, isSubmitting, markHandled, showNotification]);

  if (!open) return null;

  const activeRating = hoverRating || rating;

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-purple-100 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-prompt-title"
      >
        <div className="p-5 sm:p-6 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <div>
              <h3 id="feedback-prompt-title" className="text-lg sm:text-xl font-extrabold">
                Share your feedback
              </h3>
              <p className="text-xs sm:text-sm text-white/85 mt-0.5">
                Please rate your experience before continuing.
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-extrabold text-gray-700">
              How would you rate your experience? <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRating(value)}
                  onMouseEnter={() => setHoverRating(value)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`${value} star${value > 1 ? 's' : ''}`}
                >
                  <Star
                    size={32}
                    className={
                      value <= activeRating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-extrabold text-gray-700">Anything you&apos;d like to share? (optional)</label>
            <div className="relative">
              <MessageSquare size={16} className="absolute left-4 top-4 text-gray-400" />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you love or what we can improve…"
                rows={4}
                className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400 text-sm resize-none"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={submit}
              disabled={rating < 1 || isSubmitting}
              className={`px-5 py-3 rounded-2xl text-sm font-extrabold transition-all ${
                rating < 1 || isSubmitting
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow hover:shadow-lg'
              }`}
            >
              {isSubmitting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Submitting…
                </span>
              ) : (
                'Submit feedback'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPrompt;
